import React, { FC } from 'react';
import { Props } from './types';
import { getPageClassName, getPageBodyClassName } from './utils';
import { AlertMessage, AlertMessageType } from './../AlertMessage/component';
import { AlertMessageTypes } from '../../constants/index';
import { PageFooter } from './../PageFooter';
import { PageHeader } from './../PageHeader';
import { ExplainerText } from './../ExplainerText';
import { PageWrapper } from '../../components/Layout/PageWrapper';
import { Header } from '../../components/Layout/Header';
import { Section } from '../../components/Layout/Section';
import { Wrapper } from './../Layout/Wrapper';
import { Footer } from './../Layout/Footer';

/** renders the standard page layout that is used in Foundry */
export const Page: FC<Props> = ({
  actionButton,
  backButton,
  banner,
  children,
  className,
  clearMessages,
  contentType = 'fullwidth',
  errorMessage,
  footerButtonBarClass,
  footerButtons,
  footerClass,
  headingClass,
  headerTabs,
  icon,
  instructionalText,
  nestedPage,
  overrideTitle,
  pageType = 'parent',
  pageBodyClassName,
  showAlertMessages = true,
  showHeader = true,
  successMessage,
  title,
  toolbar,
  usePageWrapper,
}) => {
  const renderAlertMessage = (messageType: AlertMessageType, message?: string) => {
    if (!message || !showAlertMessages) {
      return null;
    } else {
      return (
        <AlertMessage message={message} messageType={messageType} visible={!!message} clearMessages={clearMessages} />
      );
    }
  };

  const renderPageHeader = () => {
    if (!showHeader) {
      return null;
    } else if (usePageWrapper) {
      return (
        <Header back={backButton} button={actionButton} title={title} icon={icon}>
          {headerTabs}
          {toolbar}
          {banner}
        </Header>
      );
    } else {
      return (
        <PageHeader
          actionButton={actionButton}
          backButton={backButton}
          title={title}
          icon={icon}
          banner={banner}
          headerTabs={headerTabs}
          headingClass={headingClass}
          overrideTitle={overrideTitle}
          toolbar={toolbar}
        />
      );
    }
  };

  const renderPageFooter = () => {
    if (!footerButtons) {
      return null;
    } else if (usePageWrapper) {
      return <Footer button={footerButtons} buttonClass={footerClass} />;
    } else {
      return (
        <PageFooter
          footerButtonBarClass={footerButtonBarClass}
          footerButtons={footerButtons}
          footerClass={footerClass}
        />
      );
    }
  };

  const renderPageBody = () => {
    if (usePageWrapper) {
      return (
        <Section
          className={getPageBodyClassName({
            usePageWrapper,
            pageBodyClassName,
            banner,
            toolbar,
            footerButtons,
            headerTabs,
            showHeader,
          })}
          type={contentType}
        >
          {instructionalText && <ExplainerText>{instructionalText}</ExplainerText>}
          {children}
        </Section>
      );
    } else {
      return (
        <div
          className={getPageBodyClassName({
            usePageWrapper,
            pageBodyClassName,
            banner,
            toolbar,
            footerButtons,
            headerTabs,
            showHeader,
          })}
        >
          {instructionalText && <div className="instructional-text">{instructionalText}</div>}
          {children}
        </div>
      );
    }
  };

  const renderPageContent = () => {
    return (
      <>
        {renderAlertMessage(AlertMessageTypes.danger, errorMessage)}
        {renderAlertMessage(AlertMessageTypes.success, successMessage)}
        {renderPageHeader()}
        {renderPageBody()}
        {renderPageFooter()}
      </>
    );
  };

  if (usePageWrapper && nestedPage) {
    return <Wrapper>{renderPageContent()}</Wrapper>;
  } else if (usePageWrapper) {
    return <PageWrapper>{renderPageContent()}</PageWrapper>;
  } else {
    return <div className={getPageClassName(pageType, contentType, className)}>{renderPageContent()}</div>;
  }
};
